import { useCallback, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { appConfig } from "../config"
import { setOriginalExchangeRate } from "../redux/slices/app"
import api from "./api"
import { CROSS_RATE_CURRENCY } from "./enums"
import { CurrencyGraph, CurrencyPairRateParams, CurrencyPairRateResponse, ExchangeRate } from "./types"

export const makeCurrencyGraph = (pairs: ExchangeRate[]): CurrencyGraph => {
  const graph: CurrencyGraph = {}
  pairs.forEach(x => {
    const baseCurrency = x.symbol.slice(0, 3)
    const counterCurrency = x.symbol.slice(3, 6)

    if (graph[baseCurrency] === undefined) {
      graph[baseCurrency] = {}
    }
    if (graph[counterCurrency] === undefined) {
      graph[counterCurrency] = {}
    }

    graph[baseCurrency][counterCurrency] = x.bid
    graph[counterCurrency][baseCurrency] = 1 / x.ask
  })

  return graph
}

export const makeExchangeRateGetter = (currencyGraph: CurrencyGraph) =>
  (fromCurrency: string, toCurrency: string): number | null => {
    if (fromCurrency === 'USDT') fromCurrency = 'UST'
    if (toCurrency === 'USDT') toCurrency = 'UST'
    try {
      if (fromCurrency === toCurrency) {
        return 1
      }

      if (!currencyGraph[fromCurrency]) {
        console.error('Exchange rate unavailable: missing currency', fromCurrency)
        return null
      }

      if (currencyGraph[fromCurrency][toCurrency]) {
        return currencyGraph[fromCurrency][toCurrency]
      }

      const rate = currencyGraph[fromCurrency][CROSS_RATE_CURRENCY] * currencyGraph[CROSS_RATE_CURRENCY][toCurrency]
      if (isNaN(rate)) {
        console.error('Exchange rate unavailable: cross-rate produced NaN for', fromCurrency, '->', toCurrency)
        return null
      }
      return rate
    } catch (error) {
      console.error('Error during conversion from', fromCurrency, 'into', toCurrency, error)
      return null
    }
  }

export const useCurrencyConverter = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [currencyGraph, setCurrencyGraph] = useState<CurrencyGraph>({})

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        const response = await fetch(appConfig.RATES_URL)
        const result: ExchangeRate[] = await response.json()
        const graph = makeCurrencyGraph(result)
        setCurrencyGraph(graph)
      } catch (error) {
        console.error('Failed to fetch exchange rates:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  const getExchangeRate = makeExchangeRateGetter(currencyGraph)
  const ratesReady = Object.keys(currencyGraph).length > 0

  return { isLoading, currencyGraph, getExchangeRate, ratesReady }
}

export const useFetchExchangeRate = () => {
  const [isLoading, setIsLoading] = useState(false)

  const fetchExchangeRate = useCallback(async (from: string, to: string): Promise<number | null> => {
    try {
      setIsLoading(true)
      const response = await fetch(`${appConfig.RATES_URL}/${from}/${to}`)
      const result: number | null = await response.json()
      return result ?? null
    } catch (error) {
      console.error('Failed to fetch exchange rate:', from, '->', to, error)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  return { isLoading, fetchExchangeRate }
}

// `syncToStore` mirrors the fetched rate into the global `originalExchangeRate`
// slice, which the crypto withdraw/confirm screens read. Consumers that only
// need the local `exchangeRate` return (e.g. the PayRetailers indicative hint)
// should pass `false` so they don't clobber that shared slice.
export const useFetchCurrencyPairRate = (
  direction: CurrencyPairRateParams['direction'],
  syncToStore: boolean = true,
) => {
  const [rate, setRate] = useState<CurrencyPairRateResponse | null>(null)
  const dispatch = useDispatch()

  const fetchCurrencyPairRate = useCallback(async (amount: string, from: string, to: string) => {
    setRate(null)
    try {
      const response = await api.getCurrencyPairRate({
        amount: amount,
        direction: direction,
        from: from,
        to: to,
      })
      setRate(response)
      return response
    } catch (error) {
      console.error('Failed to fetch currency pair rate:', from, '->', to, error)
      return null
    }
  }, [direction])

  useEffect(() => {
    if (!syncToStore) return
    dispatch(setOriginalExchangeRate(rate || null))
  }, [rate, dispatch, syncToStore])

  return { fetchCurrencyPairRate, exchangeRate: rate }
}

export const useFetchBidAskRate = () => {
  const [isLoading, setIsLoading] = useState(false)

  const fetchBidAskRate = useCallback(async (from: string, to: string): Promise<number | null> => {
    try {
      setIsLoading(true)
      const response = await fetch(`${appConfig.RATES_URL}/${from}${to}`)
      const result: number | null = await response.json()
      return result ?? null
    } catch (error) {
      console.error('Failed to fetch bid/ask rate:', from, to, error)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  return { isLoading, fetchBidAskRate }
}